import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Command, CommandInput, CommandList, CommandItem } from '@/components/ui/command';
import { Badge } from '@/components/ui/badge';
import { api } from '@/services/api';

interface Person {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  documentNumber?: string;
  totalStays: number;
}

interface PersonSearchProps {
  onSelect: (person: Person | null) => void;
  placeholder?: string;
}

export function PersonSearch({ onSelect, placeholder }: PersonSearchProps) {
  const [search, setSearch] = useState('');
  
  const { data: persons, isLoading } = useQuery({
    queryKey: ['persons', 'search', search],
    queryFn: async () => {
      const response = await api.get(`/persons/search`, {
        params: { q: search }
      });
      return response.data;
    },
    enabled: search.length > 2
  });
  
  return (
    <Command className="rounded-lg border shadow-md">
      <CommandInput
        placeholder={placeholder || "Buscar por DNI, nombre o email..."}
        value={search}
        onValueChange={setSearch}
      />
      
      {isLoading && (
        <div className="p-4 text-center text-sm text-muted-foreground">
          Buscando...
        </div>
      )}
      
      <CommandList>
        {persons?.length === 0 && search.length > 2 && (
          <div className="p-4 text-center text-sm">
            No se encontraron personas
          </div>
        )}
        
        {persons?.map((person: Person) => (
          <CommandItem
            key={person.id}
            onSelect={() => {
              onSelect(person);
              setSearch('');
            }}
            className="flex items-center justify-between p-2"
          >
            <div className="flex flex-col">
              <span className="font-medium">
                {person.firstName} {person.lastName}
              </span>
              <span className="text-sm text-muted-foreground">
                {person.email || 'Sin email'}
              </span>
            </div>
            
            <div className="flex items-center gap-2">
              {person.documentNumber && (
                <Badge variant="outline">{person.documentNumber}</Badge>
              )}
              <Badge variant="secondary">
                {person.totalStays} estancias
              </Badge>
            </div>
          </CommandItem>
        ))}
        
        {search.length > 2 && (
          <CommandItem
            onSelect={() => {
              onSelect(null);
            }}
            className="text-primary"
          >
            + Crear nueva persona "{search}"
          </CommandItem>
        )}
      </CommandList>
    </Command>
  );
}
